import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useLiveQuery } from 'dexie-react-hooks'
import { useClub, useEngineState } from '../club/ClubProvider.tsx'
import { useMapLayers, type MapLayerRow } from '../data/geo.ts'
import type { Bounds } from '../lib/geo.ts'
import { TransportError } from '../sync/transport.ts'
import type { BaseArchive } from './MapView.tsx'
import { MapFileError, downloadMapFile, newestLayer, saveMapFile } from './tileStore.ts'

export type BaseMapState =
  | 'none'          // no map has been published for this club yet
  | 'missing'       // published, not on this device yet
  | 'downloading'
  | 'ready'         // the newest archive is on this device
  | 'stale'         // an older archive is on this device (and in use) while a newer one is published
  | 'error'

const asBounds = (b: number[] | null): Bounds | null => (b && b.length === 4 ? [b[0]!, b[1]!, b[2]!, b[3]!] : null)

/** The aerial base of the map: which archive is published, whether it is on this device, how to get it, and the archive in use. The download is a
 *  deliberate act (tens of megabytes), except on Wi-Fi or a cable where the browser says so: then a newer archive simply arrives. */
export function useBaseMap() {
  const { db, transport } = useClub()
  const online = useEngineState().online !== false
  const layers = useMapLayers()
  const current = useMemo(() => newestLayer(layers ?? []), [layers])
  const files = useLiveQuery(() => db.map_file.toArray(), [db])
  const [progress, setProgress] = useState<{ done: number; total: number } | null>(null)
  const [error, setError] = useState<MapFileError['code'] | 'offline' | 'denied' | null>(null)
  const busy = useRef(false)

  const stored = useMemo(() => files?.find((f) => f.sha256 === current?.sha256), [files, current?.sha256])
  const fallback = useMemo(() => (stored ? undefined : files?.[0]), [files, stored])
  const inUse = stored ?? fallback
  const usedLayer: MapLayerRow | undefined = inUse ? (layers ?? []).find((l) => l.sha256 === inUse.sha256) ?? current : undefined
  const archive = useMemo<BaseArchive | null>(() => (inUse && usedLayer ? {
    key: inUse.sha256, blob: inUse.blob, attribution: usedLayer.attribution, minZoom: usedLayer.min_zoom, maxZoom: usedLayer.max_zoom, bounds: asBounds(usedLayer.bounds),
  } : null), [inUse, usedLayer])

  const download = useCallback(async () => {
    if (!current || busy.current) return
    if (!online) { setError('offline'); return }
    busy.current = true; setError(null); setProgress({ done: 0, total: current.bytes })
    try {
      const url = await transport.mapFileUrl(current.storage_key)
      const blob = await downloadMapFile({ url, bytes: current.bytes, sha256: current.sha256, onProgress: (done, total) => setProgress({ done, total }) })
      await saveMapFile(db, current, blob)
    } catch (e) {
      setError(e instanceof MapFileError ? e.code : e instanceof TransportError && e.kind === 'network' ? 'network' : 'denied')
    } finally { busy.current = false; setProgress(null) }
  }, [current, online, transport, db])

  // a newer archive arrives by itself on Wi-Fi or a cable; anywhere else the user decides
  const tried = useRef<string | null>(null)
  useEffect(() => {
    if (!current || files === undefined || stored || !online || tried.current === current.sha256) return
    const type = (navigator as Navigator & { connection?: { type?: string; saveData?: boolean } }).connection
    if (type?.saveData || (type?.type !== 'wifi' && type?.type !== 'ethernet')) return
    tried.current = current.sha256
    void download()
  }, [current, files, stored, online, download])

  const state: BaseMapState = !current ? 'none' : progress ? 'downloading' : error && !stored ? 'error' : stored ? 'ready' : fallback ? 'stale' : files === undefined ? 'none' : 'missing'
  return { layer: current, state, archive, progress, error, download, online, loading: layers === undefined || files === undefined }
}
